import 'isomorphic-fetch';
import WebPushNotificationsApiError from './WebPushNotificationsApiError';
import reportErrorToSentry from '~/utils/reportErrorToSentry';
import { applyPathParamsToUrl, buildQueryParamsString } from '../../common/utils';
import {
  formatSaveBrowserSearchRequest,
  formatUpdateBrowserSearchRequest,
  formatSuscribeUserRequest,
  formatUpdateUserSubscription,
  addVVEmptyString,
} from '../utils';

const ENDPOINTS = {
  SUBSCRIPTION: '/notifications/browser/subscription',
  GET_SEARCHES: '/notifications/browser/subscription/{subscriptionId}',
  GET_FILTERED_SEARCHES: '/notifications/browser/all_subscription/{subscriptionId}',
  UPDATE_USER: '/notifications/browser/subscription/{subscriptionId}',
  SAVE_SEARCH: '/notifications/browser/subscription/{subscriptionId}/searches',
  EDIT_SEARCH: '/notifications/browser/subscription/{subscriptionId}/searches/{searchId}',
  DISABLE_ALL_NOTIFICATIONS: '/notifications/browser/disable_all_subscriptions/{subscriptionId}',
  UPDATE_SEARCH_NOTIFICATION:
    '/notifications/browser/subscription/{subscriptionId}/searches/{searchId}/operation/{operation}',
  DELETE_SEARCH_NOTIFICATION:
    '/notifications/browser/subscription/{subscriptionId}/searches/{searchId}/operation/delete',
  SUBSCRIBE_USER: '/v3/notifications/add/user',
};

const FETCH_OPTIONS = {
  mode: 'cors',
  headers: {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
  },
};

export default class WebPushNotificationsApi {
  constructor(urlBase) {
    this.urlBase = urlBase;
  }

  static get(url) {
    return fetch(url, { method: 'GET', ...FETCH_OPTIONS })
      .then(response => this.handleResponse(response))
      .catch(error => this.handleError(error));
  }

  static post(url, body = null, fetchOptions = FETCH_OPTIONS) {
    return fetch(url, { method: 'POST', body, ...fetchOptions })
      .then(response => this.handleResponse(response))
      .catch(error => this.handleError(error));
  }

  static patch(url, body = null) {
    return fetch(url, { method: 'PATCH', body, ...FETCH_OPTIONS })
      .then(response => this.handleResponse(response))
      .catch(error => this.handleError(error));
  }

  static handleResponse(response) {
    return response.json().then(json => {
      if (json.errors) {
        const error = Array.isArray(json.errors) ? json.errors[0] : json.errors;
        const errorMessage = error.detail;
        throw new WebPushNotificationsApiError(error.status, errorMessage, response.status);
      }

      return json;
    });
  }

  static handleError(error) {
    reportErrorToSentry(error);
    throw error;
  }

  suscribeUser(data) {
    const { countryCode, verticalId, installationId, searchParameters, subscription } = data;
    const request = formatSuscribeUserRequest({
      countryCode,
      verticalId,
      installationId,
      searchParameters,
      subscription,
    });
    const url = `${this.urlBase}${ENDPOINTS.SUBSCRIPTION}`;
    const body = JSON.stringify(request);

    return WebPushNotificationsApi.post(url, body);
  }

  saveSearch(data) {
    const { searchParameters, subscriptionId, countryCode, verticalId } = data;
    const request = formatSaveBrowserSearchRequest({
      searchParameters,
      subscriptionId,
      countryCode,
      verticalId,
    });
    const resource = `${this.urlBase}${ENDPOINTS.SAVE_SEARCH}`;
    const pathParams = {
      subscriptionId,
    };
    const url = applyPathParamsToUrl(resource, pathParams);
    const body = JSON.stringify(request);

    return WebPushNotificationsApi.post(url, body);
  }

  editSearch(data) {
    const { searchParameters, subscriptionId, searchId, isActive } = data;
    const request = formatUpdateBrowserSearchRequest({ searchParameters, searchId, isActive });
    const resource = `${this.urlBase}${ENDPOINTS.EDIT_SEARCH}`;
    const pathParams = {
      subscriptionId,
      searchId,
    };
    const url = applyPathParamsToUrl(resource, pathParams);
    const body = JSON.stringify(request);

    return WebPushNotificationsApi.patch(url, body);
  }

  getSearches(subscriptionId) {
    const resource = `${this.urlBase}${ENDPOINTS.GET_SEARCHES}`;
    const pathParams = {
      subscriptionId,
    };
    const url = applyPathParamsToUrl(resource, pathParams);
    return WebPushNotificationsApi.get(url);
  }

  updateUserSubscription(data) {
    const { subscriptionId, subscription } = data;
    const resource = `${this.urlBase}${ENDPOINTS.UPDATE_USER}`;
    const pathParams = { subscriptionId };
    const request = formatUpdateUserSubscription(subscriptionId, subscription);

    const url = applyPathParamsToUrl(resource, pathParams);
    const body = JSON.stringify(request);

    return WebPushNotificationsApi.patch(url, body);
  }

  getSearchNotifications(options) {
    const baseUrl = `${this.urlBase}${ENDPOINTS.GET_FILTERED_SEARCHES}`;
    const { subscriptionId, verticalId, countryCode } = options;

    const composedPath = applyPathParamsToUrl(baseUrl, { subscriptionId });
    const queryParamsString = buildQueryParamsString({
      vertical_id: verticalId,
      country_code: countryCode,
    });

    const url = `${composedPath}?${queryParamsString}`;

    return WebPushNotificationsApi.get(url);
  }

  deleteSearchNotification(options) {
    const { subscriptionId, searchNotificationId, countryCode, verticalId, trackingCode } = options;

    const baseUrl = `${this.urlBase}${ENDPOINTS.DELETE_SEARCH_NOTIFICATION}`;
    const composedPath = applyPathParamsToUrl(baseUrl, {
      subscriptionId,
      searchId: searchNotificationId,
    });
    const body = `${buildQueryParamsString({
      country_code: countryCode,
      vertical_id: verticalId,
      vv: trackingCode,
    })}${addVVEmptyString(trackingCode)}`;

    return WebPushNotificationsApi.patch(composedPath, body);
  }

  updateSearchNotification(options) {
    const {
      subscriptionId,
      searchNotificationId,
      enabled,
      countryCode,
      verticalId,
      trackingCode,
    } = options;

    const baseUrl = `${this.urlBase}${ENDPOINTS.UPDATE_SEARCH_NOTIFICATION}`;
    const composedPath = applyPathParamsToUrl(baseUrl, {
      subscriptionId,
      searchId: searchNotificationId,
      operation: enabled ? 'enabled' : 'disabled',
    });

    const body = `${buildQueryParamsString({
      country_code: countryCode,
      vertical_id: verticalId,
      vv: trackingCode,
    })}${addVVEmptyString(trackingCode)}`;

    return WebPushNotificationsApi.patch(composedPath, body);
  }

  disableAllSearchNotifications(options) {
    const baseUrl = `${this.urlBase}${ENDPOINTS.DISABLE_ALL_NOTIFICATIONS}`;
    const { subscriptionId, verticalId, countryCode } = options;

    const url = applyPathParamsToUrl(baseUrl, { subscriptionId });
    const body = buildQueryParamsString({
      vertical_id: verticalId,
      country_code: countryCode,
    });

    return WebPushNotificationsApi.patch(url, body);
  }

  subscribeUserV3(data) {
    const url = `${this.urlBase}${ENDPOINTS.SUBSCRIBE_USER}`;
    const body = buildQueryParamsString(data);

    const fetchOptions = {
      ...FETCH_OPTIONS,
      headers: {
        ...FETCH_OPTIONS.headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    return WebPushNotificationsApi.post(url, body, fetchOptions);
  }
}



// WEBPACK FOOTER //
// ./app/sdk/notifications/webPush/ApiNotifications.js